import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import apiService from '../services/api';
import { getCityCoordinates, isCityInDatabase } from '../data/citiesCoordinates';
import cityCoordsFallback from '../data/cityCoordsFallback.json';
import 'leaflet/dist/leaflet.css';
import './EventsMap.css';

type MapEvent = {
  id: number | string;
  name: string;
  city: string;
  date: string;
  endDate?: string;
  time?: string;
  type?: string;
  address?: string;
  link?: string;
  multiDay?: boolean;
  isMultiDay?: boolean;
  coordinates?: [number, number] | string;
};

type MapPoint = {
  key: string;
  city: string;
  lat: number;
  lng: number;
  events: MapEvent[];
};

const DOZYNKI_RX = /do[żz]ynk|plonów|\bplony\b|\bplon\b|święto\s+plon/i;
const WINDOW_DAYS = 30;

const TYPE_EMOJIS: Record<string, string> = {
  Koncert: '🎵',
  Kultura: '🎭',
  Jedzenie: '🍽️',
  Festiwal: '🎪',
  Festival: '🎪',
  Bary: '🍻',
  Sport: '⚽',
  Nauka: '🔬',
  Kino: '🎬',
  Targi: '🏪',
  Kluby: '🎤',
  Impreza: '🎉',
  Wystawa: '🖼️',
  Teatr: '🎭',
  Edukacja: '🔬',
  Biznes: '💼',
  Inne: '🎯',
};

function getTypeEmoji(type?: string): string {
  if (!type) return '🎯';
  return TYPE_EMOJIS[type] || '🎯';
}

const iconByEmoji = new Map<string, L.DivIcon>();

function getMarkerIcon(type?: string): L.DivIcon {
  const emoji = getTypeEmoji(type);
  const cached = iconByEmoji.get(emoji);
  if (cached) return cached;
  const icon = L.divIcon({
    className: 'events-map-marker',
    html: `<div class="events-map-marker-inner" title="${type || 'Wydarzenie'}">${emoji}</div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 36],
    popupAnchor: [0, -32],
  });
  iconByEmoji.set(emoji, icon);
  return icon;
}

function toDateOnly(value: string | Date): Date {
  const d = typeof value === 'string' ? new Date(`${value.split('T')[0]}T12:00:00`) : new Date(value);
  d.setHours(12, 0, 0, 0);
  return d;
}

function formatYmd(d: Date): string {
  return d.toISOString().split('T')[0];
}

function addDays(d: Date, days: number): Date {
  const next = new Date(d);
  next.setDate(next.getDate() + days);
  return next;
}

function parseCoordinates(event: MapEvent): [number, number] | null {
  const raw = event.coordinates;
  if (Array.isArray(raw) && raw.length >= 2) {
    const lat = Number(raw[0]);
    const lng = Number(raw[1]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
  }
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length >= 2) {
        const lat = Number(parsed[0]);
        const lng = Number(parsed[1]);
        if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
      }
    } catch {
      /* ignore */
    }
  }
  const city = String(event.city || '').trim();
  if (city && isCityInDatabase(city)) {
    const [lat, lng] = getCityCoordinates(city);
    return [lat, lng];
  }

  const fallback =
    (cityCoordsFallback as Record<string, { lat: number; lng: number }>)[city] ||
    (cityCoordsFallback as Record<string, { lat: number; lng: number }>)[city.toLowerCase()];
  if (fallback && Number.isFinite(fallback.lat) && Number.isFinite(fallback.lng)) {
    return [fallback.lat, fallback.lng];
  }

  return null;
}

function eventTouchesDay(event: MapEvent, day: string): boolean {
  const start = formatYmd(toDateOnly(event.date));
  const end =
    (event.isMultiDay || event.multiDay) && event.endDate
      ? formatYmd(toDateOnly(event.endDate))
      : start;
  return day >= start && day <= end;
}

function eventInWindow(event: MapEvent, from: string, to: string): boolean {
  const start = formatYmd(toDateOnly(event.date));
  const end =
    (event.isMultiDay || event.multiDay) && event.endDate
      ? formatYmd(toDateOnly(event.endDate))
      : start;
  return start <= to && end >= from;
}

function formatEventWhen(event: MapEvent): string {
  const multi = !!(event.isMultiDay || event.multiDay);
  if (multi && event.endDate && event.endDate !== event.date) {
    return `${event.date} → ${event.endDate}${event.time ? ` · ${event.time}` : ''}`;
  }
  return event.time ? `${event.date} ${event.time}` : event.date;
}

function buildPoints(events: MapEvent[]): MapPoint[] {
  const byKey = new Map<string, MapPoint>();

  for (const event of events) {
    const coords = parseCoordinates(event);
    if (!coords) continue;
    const [lat, lng] = coords;
    const city = String(event.city || '').trim() || 'Nieznane';
    const key = `${city}|${lat.toFixed(4)}|${lng.toFixed(4)}`;
    if (!byKey.has(key)) {
      byKey.set(key, { key, city, lat, lng, events: [] });
    }
    byKey.get(key)!.events.push(event);
  }

  const points = Array.from(byKey.values());
  const seenXY = new Map<string, number>();
  for (const point of points) {
    const xy = `${point.lat.toFixed(4)},${point.lng.toFixed(4)}`;
    const n = seenXY.get(xy) || 0;
    seenXY.set(xy, n + 1);
    if (n > 0) {
      point.lat = point.lat + 0.012 * Math.cos(n * 1.7);
      point.lng = point.lng + 0.012 * Math.sin(n * 1.7);
    }
  }

  return points.sort((a, b) => a.city.localeCompare(b.city, 'pl'));
}

export default function EventsMap() {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(12, 0, 0, 0);
    return d;
  }, []);
  const windowFrom = formatYmd(today);
  const windowTo = formatYmd(addDays(today, WINDOW_DAYS));

  const [events, setEvents] = useState<MapEvent[]>([]);
  const [selectedDay, setSelectedDay] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError('');
      try {
        const data = await apiService.getEvents();
        if (!cancelled) setEvents(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        if (!cancelled) setError('Nie udało się pobrać wydarzeń.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const windowEvents = useMemo(() => {
    return events.filter((event) => {
      if (!event?.date || !event?.name) return false;
      if (DOZYNKI_RX.test(event.name)) return false;
      return eventInWindow(event, windowFrom, windowTo);
    });
  }, [events, windowFrom, windowTo]);

  const visibleEvents = useMemo(() => {
    if (!selectedDay) return windowEvents;
    return windowEvents.filter((event) => eventTouchesDay(event, selectedDay));
  }, [windowEvents, selectedDay]);

  const points = useMemo(() => buildPoints(visibleEvents), [visibleEvents]);
  const center: [number, number] = [52.1, 19.4];

  return (
    <div className="events-map-page">
      <header className="events-map-header">
        <div className="events-map-header-top">
          <Link to="/" className="events-map-back">
            ← Wróć
          </Link>
          <h1>
            <span aria-hidden="true">🗺️</span> Mapa wydarzeń
          </h1>
        </div>
        <p className="events-map-subtitle">
          Najbliższe {WINDOW_DAYS} dni ({windowFrom} → {windowTo})
        </p>
        <div className="events-map-filters">
          <label className="events-map-day-label" htmlFor="events-map-day">
            Dzień
          </label>
          <input
            id="events-map-day"
            type="date"
            className="events-map-day-input"
            min={windowFrom}
            max={windowTo}
            value={selectedDay}
            onChange={(e) => setSelectedDay(e.target.value)}
          />
          <button
            type="button"
            className="events-map-day-clear"
            onClick={() => setSelectedDay('')}
            disabled={!selectedDay}
          >
            Cały miesiąc
          </button>
          <span className="events-map-count">
            {isLoading
              ? 'Ładowanie…'
              : `${visibleEvents.length} wydarzeń · ${points.length} miejsc`}
          </span>
        </div>
      </header>

      <div className="events-map-wrap">
        {error ? (
          <div className="events-map-empty">
            <p>{error}</p>
          </div>
        ) : isLoading ? (
          <div className="events-map-empty">
            <p>Ładowanie mapy… ⏳</p>
          </div>
        ) : points.length === 0 ? (
          <div className="events-map-empty">
            <p>Brak wydarzeń do pokazania na mapie w wybranym terminie.</p>
          </div>
        ) : (
          <MapContainer
            center={center}
            zoom={6}
            minZoom={5}
            maxZoom={14}
            scrollWheelZoom
            className="events-map"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {points.map((point) => (
              <Marker
                key={point.key}
                position={[point.lat, point.lng]}
                icon={getMarkerIcon(point.events[0]?.type)}
              >
                <Popup>
                  <div className="events-map-popup">
                    <strong className="events-map-popup-city">{point.city}</strong>
                    <ul className="events-map-popup-list">
                      {point.events.map((ev) => (
                        <li key={`${ev.id}-${ev.date}-${ev.name}`}>
                          <div className="events-map-popup-name">
                            <span aria-hidden="true">{getTypeEmoji(ev.type)} </span>
                            {ev.name}
                          </div>
                          <div className="events-map-popup-meta">{formatEventWhen(ev)}</div>
                          {ev.type ? (
                            <div className="events-map-popup-meta">{ev.type}</div>
                          ) : null}
                          {ev.address ? (
                            <div className="events-map-popup-addr">{ev.address}</div>
                          ) : null}
                          {ev.link ? (
                            <a href={ev.link} target="_blank" rel="noopener noreferrer">
                              Więcej →
                            </a>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        )}
      </div>
    </div>
  );
}
